/**
 * Custom ChartRendererService
 * Overrides the shared library service to use custom components
 */
import { Injectable, ViewContainerRef, inject } from '@angular/core';
import { Router } from '@angular/router';

// Import custom overrides (bar-chart, pie-chart, bubble-chart with custom styling)
import { BarChartComponent } from '../chart-components/bar-chart/bar-chart.component';
import { PieChartComponent } from '../chart-components/pie-chart/pie-chart.component';
import { BubbleChartComponent } from '../chart-components/bubble-chart/bubble-chart.component';
import { CustomHorizontalBarChartComponent } from '../chart-components/horizontal-bar-chart/horizontal-bar-chart.component';
import { CatastrophicFailureHorizontalBarChartComponent } from '../chart-components/horizontal-bar-chart/catastrophic-failure-horizontal-bar-chart.component';
import { DonutChartComponent } from '../chart-components/donut-chart/donut-chart.component';
// note: use the shared/global-components/global-filter import below
// Import remaining components from shared library
import { LineChartComponent } from '../chart-components/line-chart/line-chart.component';
import { WorldMapComponent } from '../chart-components/world-map/world-map.component';
import { CountComponent } from '@shared-lib/chart-components/step-line-chart/count.component';
import { StackedAreaChartComponent } from '@shared-lib/chart-components/stacked-area-chart/stacked-area-chart.component';
import { WaterfallChartComponent } from '@shared-lib/chart-components/waterfall-chart/waterfall-chart.component';
import { QuadBubbleChartComponent } from '@shared-lib/chart-components/quad-bubble-chart/quad-bubble-chart.component';
import { DrillDownComponent } from '@shared-lib/chart-components/drill-down/drill-down.component';
import { HorizontalBarChartComponent as SharedHorizontalBarChartComponent } from '@shared-lib/chart-components/horizontal-bar-chart/horizontal-bar-chart.component';
import { TreemapChartComponent } from '@shared-lib/chart-components/treemap-chart/treemap-chart.component';
import { LineColumnChartComponent } from '@shared-lib/chart-components/line-column-chart/line-column-chart.component';
import { CombinedChartComponent } from '@shared-lib/chart-components/combined-chart/combined-chart.component';
import { MatSliderComponent } from '@shared-lib/global-components/mat-slider/mat-slider.component';
import { WaterfallStackChartComponent } from '@shared-lib/chart-components/waterfall-stack-chart/waterfall-stack-chart.component';
import { InputBoxComponent } from '@shared-lib/global-components/input-box/input-box.component';
import { TableComponent } from '@shared-lib/global-components/table/table.component';
import { NotesComponent } from '../global-components/notes/notes';
import { EditableTableComponent } from '@shared-lib/chart-components/editable-table/editable-table.component';
import { DynamicEditTableComponent } from '@shared-lib/chart-components/dynamic-edit-table/dynamic-edit-table.component';
import { UploadFileComponent } from '@shared-lib/chart-components/upload-file/upload-file.component';
import { NumberStatsComponent } from '../../projects/my-app/src/app/number-stats/number-stats.component';
import { StackBarChartComponent } from '../chart-components/stack-bar-chart/stack-bar-chart.component';
import { GenericStackbarChart } from '@shared-lib/chart-components/generic-stackbar-chart/generic-stackbar-chart';
import { GenericTableComponent } from '@shared-lib/chart-components/generic-table/generic-table';
import { ComparisonBarChartComponent } from '../chart-components/comparison-bar-chart/comparison-bar-chart.component';
import { TcoHeaderComponent } from '../chart-components/tco-header/tco-header.component';
import { TreeDrillDownComponent } from '@custom';
import { GlobalFilterComponent } from '../global-components/global-filter/global-filter.component';
import { RepowerDowntimeStatusTableComponent } from '../chart-components/table/table.component';
import { StackedHorizontalBarChartComponent } from '../chart-components/stacked-horizontal-bar-chart/stacked-horizontal-bar-chart.component';
// import { DonutChartComponent } from '@shared-lib/chart-components/donut-chart/donut-chart.component';

@Injectable({
  providedIn: 'root'
})
export class ChartRendererService {
  private router = inject(Router);

  renderChart(
    type: string,
    viewContainerRef: ViewContainerRef,
    chartData: any
  ): void {
    viewContainerRef.clear();

    let component: any;

    switch (type) {
      case 'count':
        component = CountComponent;
        break;
      case 'line-chart':
        // CUSTOM: Uses custom LineChartComponent that parses nested responseData
        component = LineChartComponent;
        break;
      case 'comparison-bar-chart':
        component = ComparisonBarChartComponent;
        break;
      case 'tco-header':
        component = TcoHeaderComponent;
        break;
      case 'bar-chart':
        // CUSTOM: Uses custom BarChartComponent with pink/red color
        component = BarChartComponent;
        break;
      case 'pie-chart':
        // CUSTOM: Uses custom PieChartComponent with 70% radius
        component = PieChartComponent;
        break;
      case 'number-stats':
        component = NumberStatsComponent;
        break;
      case 'bubble-chart':
        // CUSTOM: Uses custom BubbleChartComponent with pink color
        component = BubbleChartComponent;
        break;
      case 'world-map':
        // SHARED: World map choropleth component from shared library
        component = WorldMapComponent;
        break;
      case 'stacked-area-chart':
        component = StackedAreaChartComponent;
        break;
      case 'waterfall-chart':
        component = WaterfallChartComponent;
        break;
      case 'quad-bubble-chart':
        component = QuadBubbleChartComponent;
        break;
      case 'drill-down':
        component = DrillDownComponent;
        break;
      case 'horizontal-bar-chart':
        // Route-specific horizontal bar chart component selection
        const isOnCatastrophicFailure = this.router.url.includes('catastrophic-failure');
        component = isOnCatastrophicFailure 
          ? CatastrophicFailureHorizontalBarChartComponent 
          : CustomHorizontalBarChartComponent;
        break;
      case 'treemap-chart':
        component = TreemapChartComponent;
        break;
      case 'line-column-chart':
        component = LineColumnChartComponent;
        break;
      case 'slider':
        component = MatSliderComponent;
        break;
      case 'global-year-filter':
        component = GlobalFilterComponent;
        break;
      case 'combined-chart':
        component = CombinedChartComponent;
        break;
      case 'waterfall-stack':
        component = WaterfallStackChartComponent;
        break;
      case 'input-box':
        component = InputBoxComponent;
        break;
      case 'global-filter':
        component = GlobalFilterComponent;
        break;
      case 'editable-table':
        component = EditableTableComponent;
        break;
      case 'table':
        component = TableComponent;
        break;
      case 'notes':
        component = NotesComponent;
        break;
      case 'stack-bar-chart':
        component = StackBarChartComponent;
        break;
      case 'dynamic-edit-table':
        component = DynamicEditTableComponent;
        break;
      case 'upload-file':
        component = UploadFileComponent;
        break;
      // Generic components (use utilities for all functionality)
      case 'generic-stack-bar-chart':
      case 'stack-bar-chart-generic':
        component = GenericStackbarChart;
        break;
      case 'generic-table':
      case 'table-generic':
        component = GenericTableComponent;
        break;
      case 'tree-drill-down':
        component = TreeDrillDownComponent;
        break;
      case 'repower-downtime-status-table':
        component = RepowerDowntimeStatusTableComponent;
        break;
      case 'donut-chart':
        component = DonutChartComponent;
        break;
      case 'stacked-horizontal-bar-chart':
        component = StackedHorizontalBarChartComponent;
        break;
      default:
        console.error(`Unknown chart type: ${type}`);
        return;
    }

    const componentRef = viewContainerRef.createComponent(component);

    if (componentRef.instance) {
      (componentRef.instance as any).data = chartData;
    }
  }
}
